/* eslint-disable no-console */
import * as RealMsalBrowser from "@azure/msal-browser";

import { devSettings, isDev } from "../dev"; // Adjust import path as needed

import { heatAuthService } from "./heat-auth-service";

/**
 * Mode can be: 'msal' | 'heatAuth' | 'localOnly'
 * - 'msal': Use real MSAL
 * - 'heatAuth': Use Heat Auth stubs
 * - 'localOnly': Minimal local auth or none
 */
const MODE: "msal" | "heatAuth" | "localOnly" =
    (process.env.REACT_APP_AUTH_MODE as any) || "heatAuth";

/**
 * Toggle detailed console logs via REACT_APP_AUTH_DEBUG
 * Set to "true" to enable, otherwise disabled
 */
const LOG_ENABLED = process.env.REACT_APP_AUTH_DEBUG === "true";

function debugLog(...args: any[]) {
    if (LOG_ENABLED) {
        console.log(...args);
    }
}

/* ------------------------------------------------------------------
   Shared re-exports or placeholder stubs for types / enums / errors
------------------------------------------------------------------ */

export const LogLevel = RealMsalBrowser.LogLevel;

export type Configuration = RealMsalBrowser.Configuration;

export enum EventType {
    LOGOUT_SUCCESS = "msal:logoutSuccess",
}

export class InteractionRequiredAuthError extends Error {
    constructor(message?: string) {
        super(message);
        this.name = "InteractionRequiredAuthError";
    }
}

export type AccountInfo = RealMsalBrowser.AccountInfo;

export type EventMessage = RealMsalBrowser.EventMessage;

/* ------------------------------------------------------------------
   Helper function to create a fake JWT (for stubbing)
------------------------------------------------------------------ */
function createFakeJwt(claims: object): string {
    const header = { alg: "HS256", typ: "JWT" };

    const base64UrlEncode = (obj: object): string => {
        const json = JSON.stringify(obj);
        // Convert to Base64
        const base64 = btoa(unescape(encodeURIComponent(json)));

        // Convert to Base64URL
        return base64.replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_");
    };

    const encodedHeader = base64UrlEncode(header);
    const encodedPayload = base64UrlEncode(claims);
    const signature = "stub-signature"; // Not used in validation

    return `${encodedHeader}.${encodedPayload}.${signature}`;
}

class StubPublicClientApplication {
    private activeAccount: AccountInfo | null = null;

    constructor(config: Configuration) {
        debugLog("[StubPublicClientApplication] Initialized with config:", config);
    }

    setActiveAccount(account: AccountInfo | null): void {
        this.activeAccount = account;
        debugLog("[StubPublicClientApplication] setActiveAccount called with:", account);
    }

    getActiveAccount(): AccountInfo | null {
        debugLog(
            "[StubPublicClientApplication] getActiveAccount called, returning:",
            this.activeAccount,
        );

        return this.activeAccount;
    }

    addEventCallback(_callback: (message: EventMessage) => void): string {
        debugLog("[StubPublicClientApplication] addEventCallback called");

        return "stub-callback-id";
    }

    removeEventCallback(callbackId: string): void {
        debugLog("[StubPublicClientApplication] removeEventCallback called with ID:", callbackId);
    }

    /**
     * If isDev is true, skip the redirect.
     * Otherwise continue the normal stub logic.
     */
    async loginRedirect(request: any): Promise<void> {
        debugLog("[StubPublicClientApplication] loginRedirect called with request:", request);

        // If dev, skip the redirect
        if (isDev) {
            debugLog("[StubPublicClientApplication] isDev === true, skipping redirect");

            return;
        }

        // Otherwise, do normal stub logic
        const urlParams = new URLSearchParams(window.location.search);
        const pauseParam = urlParams.get("pause");

        if (pauseParam !== "true") {
            if (isDev) {
                console.warn(
                    "You are unauthenticated but running in dev mode, please set the appropriate token.",
                );

                return;
            }

            window.location.href = "/login";
        }
    }

    async logoutRedirect(logoutRequest?: any): Promise<void> {
        debugLog(
            "[StubPublicClientApplication] logoutRedirect called with request:",
            logoutRequest,
        );
        heatAuthService.logout();
    }

    /**
     * Called in code for retrieving tokens silently
     * If isDev, just return the devSettings token for both access & legacy
     */
    async acquireTokenSilent(
        request: any,
    ): Promise<{ accessToken: string; legacyToken: string; jwt: string; nextToken: string }> {
        debugLog("[StubPublicClientApplication] acquireTokenSilent called with request:", request);

        if (isDev) {
            // In dev mode, we do not use heatAuth, only the dev token
            debugLog("[StubPublicClientApplication] isDev === true, returning devSettings token");
            const devToken = devSettings.api.token ?? "dev-default-token";

            // Construct a simple fake JWT with minimal claims
            const devJwt = createFakeJwt({ sub: "devUser", email: "dev@example.com" });

            // Optionally set the active account to dev user
            this.setActiveAccount({
                homeAccountId: "devUserId",
                environment: "devEnv",
                tenantId: "devTenant",
                username: "devUser",
                localAccountId: "devUserLocal",
                authorityType: "B2C",
                idTokenClaims: {
                    sub: "devUser",
                    preferred_username: "devUser",
                    email: "dev@example.com",
                },
            });

            return {
                accessToken: devToken,
                legacyToken: devToken,
                nextToken: devSettings.api.heatToken,
                jwt: devJwt,
            };
        }

        // In non-dev mode, use the existing heatAuth stub logic
        const accessToken = heatAuthService.getAccessToken();
        const refreshToken = heatAuthService.getRefreshToken();
        const legacyToken = heatAuthService.getLegacyToken();

        if (!accessToken || !legacyToken) {
            debugLog(
                "[StubPublicClientApplication] No access token found, throwing InteractionRequiredAuthError",
            );
            throw new InteractionRequiredAuthError("No access token found.");
        }

        try {
            console.log(`Access token: ${accessToken}`);
            const tokenData = await heatAuthService.validateToken(accessToken);
            debugLog("[StubPublicClientApplication] Token is valid:", tokenData);

            // Create a fake JWT with the necessary claims
            const fakeJwt = createFakeJwt({
                sub: tokenData.userId,
                preferred_username: tokenData.username,
                firstName: tokenData.firstName,
                lastName: tokenData.lastName,
                role: tokenData.role,
                email: tokenData.email,
            });

            // Set active account
            const accountInfo: AccountInfo = {
                homeAccountId: tokenData.userId,
                environment: "heatAuth",
                tenantId: "heatAuthTenant",
                username: tokenData.username,
                localAccountId: tokenData.userId,
                authorityType: "B2C",
                idTokenClaims: {
                    sub: tokenData.userId,
                    preferred_username: tokenData.username,
                    firstName: tokenData.firstName,
                    lastName: tokenData.lastName,
                    role: tokenData.role,
                    email: tokenData.email,
                },
            };
            this.setActiveAccount(accountInfo);

            return { accessToken, legacyToken, jwt: fakeJwt, nextToken: accessToken };
        } catch (error) {
            debugLog("[StubPublicClientApplication] Token validation failed:", error);

            if (refreshToken) {
                debugLog("[StubPublicClientApplication] Attempting to refresh token");
                try {
                    const refreshed = await heatAuthService.refreshToken(refreshToken);
                    debugLog(
                        "[StubPublicClientApplication] Token refreshed successfully:",
                        refreshed,
                    );

                    // Re-validate the new token
                    const newAccessToken = refreshed.accessToken;
                    const newTokenData = await heatAuthService.validateToken(newAccessToken);

                    // Create a fake JWT with the new token data
                    const newFakeJwt = createFakeJwt({
                        sub: newTokenData.userId,
                        preferred_username: newTokenData.username,
                        firstName: newTokenData.firstName,
                        lastName: newTokenData.lastName,
                        role: newTokenData.role,
                        email: newTokenData.email,
                    });

                    const newAccountInfo: AccountInfo = {
                        homeAccountId: newTokenData.userId,
                        environment: "heatAuth",
                        tenantId: "heatAuthTenant",
                        username: newTokenData.username,
                        localAccountId: newTokenData.userId,
                        authorityType: "B2C",
                        idTokenClaims: {
                            sub: newTokenData.userId,
                            preferred_username: newTokenData.username,
                            firstName: newTokenData.firstName,
                            lastName: newTokenData.lastName,
                            role: newTokenData.role,
                            email: newTokenData.email,
                        },
                    };
                    this.setActiveAccount(newAccountInfo);

                    return {
                        accessToken: newFakeJwt,
                        legacyToken,
                        jwt: newFakeJwt,
                        nextToken: newAccessToken,
                    };
                } catch (refreshError) {
                    debugLog("[StubPublicClientApplication] Token refresh failed:", refreshError);
                    throw new InteractionRequiredAuthError("Token refresh failed.");
                }
            }

            throw new InteractionRequiredAuthError("Token validation failed.");
        }
    }

    async acquireTokenRedirect(request: any): Promise<void> {
        debugLog(
            "[StubPublicClientApplication] acquireTokenRedirect called with request:",
            request,
        );

        if (isDev) {
            console.warn(
                "You are unauthenticated but running in dev mode, please set the appropriate token.",
            );

            return;
        }

        window.location.href = "/login";
    }
}

/**
 * Define a constructor type for PublicClientApplication
 */
type PcaConstructor = new (
    config: Configuration,
) => RealMsalBrowser.PublicClientApplication | StubPublicClientApplication;

/**
 * Export PublicClientApplication based on MODE
 */
export const PublicClientApplication: PcaConstructor =
    MODE === "msal"
        ? (RealMsalBrowser.PublicClientApplication as unknown as PcaConstructor)
        : StubPublicClientApplication;

export { StubPublicClientApplication };

debugLog(`[msal-browser.tsx] AUTH_MODE: ${MODE}`);
debugLog(`[msal-browser.tsx] Logging is ${LOG_ENABLED ? "ENABLED" : "DISABLED"}`);
